import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Media } from '@/types/media';
import { TYPE_LABELS } from '@/types/media';
import { useFavorites } from '@/hooks/useFavorites';

interface MediaCardProps {
  media: Media;
  variant?: 'grid' | 'list';
}

export default function MediaCard({ media, variant = 'grid' }: MediaCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const active = isFavorite(media.id);

  const handleFavorite = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(media.id);
  };

  if (variant === 'list') {
    return (
      <Link
        to={`/detail/${media.id}`}
        className="group flex gap-4 p-3 md:p-4 rounded-lg bg-background-50 border border-background-200/80 hover:border-primary-300 transition-colors duration-300 cursor-pointer"
      >
        <div className="relative w-[96px] h-[140px] md:w-[120px] md:h-[170px] shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={media.cover}
            alt={`${media.title} 封面`}
            title={`${media.title} - 神马视频影视资源`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-base md:text-lg font-semibold text-foreground-950 line-clamp-1">
              {media.title}
            </h3>
            <button
              type="button"
              onClick={handleFavorite}
              aria-label={active ? '取消收藏' : '收藏'}
              className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-full border transition-colors cursor-pointer ${
                active
                  ? 'bg-accent-500 border-accent-500 text-background-50'
                  : 'border-background-300 text-foreground-500 hover:text-accent-600 hover:border-accent-400'
              }`}
            >
              <i className={active ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
            </button>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs md:text-sm text-foreground-500">
            <span className="px-2 py-0.5 rounded bg-primary-100 text-primary-700">{TYPE_LABELS[media.type]}</span>
            <span>{media.region}</span>
            <span className="text-background-400">|</span>
            <span>{media.year}</span>
            <span className="text-background-400">|</span>
            <span className="text-accent-600 font-semibold">{media.rating} 分</span>
          </div>
          <p className="mt-2 text-xs md:text-sm text-foreground-600 line-clamp-2">{media.description}</p>
          <div className="mt-auto pt-2 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <i className="ri-user-star-line text-secondary-500"></i>
            <span className="truncate">主演：{media.cast.join(' / ')}</span>
            <span className="ml-auto whitespace-nowrap">{media.updateDate} 更新</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${media.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/80 hover:border-primary-300 transition-colors duration-300 cursor-pointer"
    >
      <div className="relative w-full aspect-[2/3] overflow-hidden bg-background-200">
        <img
          src={media.cover}
          alt={`${media.title} 封面`}
          title={`${media.title} - 神马视频影视资源`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-[11px] font-medium bg-primary-600/90 text-background-50">
          {TYPE_LABELS[media.type]}
        </span>
        <span className="absolute top-2 right-2 px-2 py-0.5 rounded text-[11px] font-semibold bg-accent-500/95 text-background-50">
          {media.rating} 分
        </span>
        <button
          type="button"
          onClick={handleFavorite}
          aria-label={active ? '取消收藏' : '收藏'}
          className={`absolute bottom-2 right-2 w-8 h-8 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
            active
              ? 'bg-accent-500 text-background-50'
              : 'bg-background-50/85 text-foreground-600 hover:text-accent-600'
          }`}
        >
          <i className={active ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
        </button>
      </div>
      <div className="p-3">
        <h3 className="text-sm md:text-base font-semibold text-foreground-950 line-clamp-1">{media.title}</h3>
        <p className="mt-1 text-xs text-foreground-500 line-clamp-1">
          {media.region} · {media.year} · {media.genres.join(' / ')}
        </p>
        <p className="mt-1 text-xs text-foreground-500 whitespace-nowrap">{media.updateDate} 更新</p>
      </div>
    </Link>
  );
}